import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import { useUser } from "$global-providers/user-provider";
import { useCallback } from "react";
import type { RxCollection } from "rxdb";
import { useRxData } from "rxdb-hooks";
import { linkToReviewQueueEntry } from "../../../_lib/review-queue-destination";
import { MATCH_NOTHING } from "../../../_lib/rxdb-selectors";

export const selectNextInQueue = ({
  encounters,
  reviewerId,
  excludeEncounterId,
}: {
  encounters: readonly EpocusEncounter[];
  reviewerId: string;
  excludeEncounterId: string | null;
}): EpocusEncounter | null =>
  encounters.find(
    (encounter) =>
      encounter.id !== excludeEncounterId &&
      encounter.performerId !== reviewerId &&
      (encounter.assignedReviewerId === null ||
        encounter.assignedReviewerId === reviewerId),
  ) ?? null;

export type NextInQueue = {
  encounter: EpocusEncounter;
  link: string;
};

export const useNextInQueue = (
  excludeEncounterId: string | null,
): NextInQueue | null => {
  const user = useUser();

  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: {
          status: { $eq: "pending_review" },
          performerId: { $ne: user.id },
        },
        sort: [{ date: "asc" }],
        limit: 20,
      }),
  );

  const next = selectNextInQueue({
    encounters,
    reviewerId: user.id,
    excludeEncounterId,
  });
  const nextEncounterId = next?.id ?? null;

  const examsQuery = useCallback(
    (collection: RxCollection<EpocusExam>) =>
      collection.find({
        selector: nextEncounterId
          ? { encounterId: { $eq: nextEncounterId } }
          : MATCH_NOTHING,
      }),
    [nextEncounterId],
  );
  const { result: exams } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    examsQuery,
  );

  if (!next) return null;

  return { encounter: next, link: linkToReviewQueueEntry(next.id, exams) };
};
